/**
 * **Kerangka konsol** — `Layout.Sider` + pita atas + isi, pola yang sama dengan konsol OSB Nexus.
 *
 * ⚠️ Kerangkanya dipisah dari layar-layarnya. Sebelumnya pita atas & navigasi tinggal DI DALAM
 * layar Outlet, jadi layar berikutnya harus menyalinnya — dan salinan kedua akan menyimpang pada
 * perubahan pertama.
 */
import { Avatar, Button, Layout, Menu, Space, Tag, Typography } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { logout, session } from '../api/session';
import { ChangePassword } from '../components/ChangePassword';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { Availability } from '../screens/Availability';
import { Brands } from '../screens/Brands';
import { ConsoleUsers } from '../screens/ConsoleUsers';
import { Integrations } from '../screens/Integrations';
import { Modifiers } from '../screens/Modifiers';
import { Outlets } from '../screens/Outlets';
import { Payments } from '../screens/Payments';
import { Floor } from '../screens/Floor';
import { Roles } from '../screens/Roles';
import { Staff } from '../screens/Staff';
import { Subscription } from '../screens/Subscription';
import { PriceGroups } from '../screens/PriceGroups';
import { Products } from '../screens/Products';
import { Promos } from '../screens/Promos';
import { ReceiptTemplate } from '../screens/Receipt';
import { Notifications } from '../screens/Notifications';
import { Reports } from '../screens/Reports';
import { Stock } from '../screens/Stock';
import { Routing } from '../screens/Routing';
import { Structure } from '../screens/Structure';
import { Taxes } from '../screens/Taxes';
import { NAV } from './nav';
import { onNavigate } from './navigate';

export interface ShellProps {
  readonly onLogout: () => void;
}

export function Shell({ onLogout }: ShellProps) {
  const { t } = useTranslation();
  const s = session();
  const [layar, setLayar] = useState('outlets');
  /**
   * ⭐⭐ **Sidebar menciut di layar sempit** (Imron, 2026-08-23: *"tampilan portal di HP tidak
   * enak"*).
   *
   * ⛔ Sebelum ini `Layer.Sider` selebar 236px selalu terpasang. Di HP 390px itu berarti **60%
   * layar dipakai navigasi**, dan yang tersisa untuk isinya 154px — bukan "kurang enak", melainkan
   * tak bisa dipakai sama sekali.
   *
   * ⚠️ `undefined` = breakpoint belum menjawab. Menebaknya `false` membuat sidebar berkedip
   * terbuka lalu menutup di HP; menebaknya `true` membuat ia berkedip tertutup di desktop.
   */
  /**
   * ⛔⛔ **Dijawab SINKRON di penginisialisasi, bukan di `useEffect`.**
   *
   * Versi pertama membacanya di efek — dan efek jalan SESUDAH cat pertama. Akibatnya di HP portal
   * berkedip memakai tata letak desktop dulu (sidebar 236px, halaman meluber 192px ke samping),
   * baru menata diri. Kedipannya cuma satu bingkai, tapi ia menggeser seluruh halaman — dan jari
   * yang sudah menekan sesuatu menekan benda yang berbeda.
   *
   * ⚠️ Ketahuan dari `PW3` yang mengukur `scrollWidth` **tepat sesudah `goto`**, bukan sesudah
   * halaman tenang. Uji yang menunggu dulu akan melewatkannya selamanya.
   */
  const [sempit, setSempit] = useState<boolean>(
    () => window.matchMedia('(max-width: 991px)').matches,
  );
  const [menuBuka, setMenuBuka] = useState(false);
  /**
   * ⛔ `matchMedia` sendiri, bukan `breakpoint` bawaan `Layout.Sider`.
   *
   * Bawaannya menciutkan lebar jadi 0 **tanpa membuang isinya** — menunya tetap ada di DOM, tetap
   * bisa difokus keyboard, dan tetap terbaca pembaca layar. Yang tak terlihat tapi masih bisa
   * ditekan adalah bentuk kebingungan yang paling sulit dilaporkan orang ("kok kepencet sendiri").
   * ⇒ Di layar sempit menunya benar-benar TIDAK dirender sampai diminta.
   */
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 991px)');
    const terapkan = () => { setSempit(mq.matches); setMenuBuka(false); };
    mq.addEventListener('change', terapkan);
    return () => { mq.removeEventListener('change', terapkan); };
  }, []);

  /** ⭐ Menu tersembunyi hanya bila layarnya sempit DAN belum diminta. */
  const ciut = sempit && !menuBuka;

  /*
   * ⭐ Satu-satunya jalan berpindah layar dari dalam layar lain (lihat `navigate.ts`). Dipakai
   * tautan pintas di daftar periksa kesiapan: "yang kurang" dan "tempat memperbaikinya" berada di
   * dua layar berbeda, dan menyebut namanya tanpa mengantar ke sana menyisakan pekerjaan mencari.
   */
  useEffect(() => {
    onNavigate((key) => { setLayar(key); });
    return () => { onNavigate(undefined); };
  }, []);

  const items = useMemo(
    () =>
      NAV.map((g, i) => ({
        type: 'group' as const,
        key: `g${String(i)}`,
        label: g.label === undefined ? undefined : t(g.label),
        children: g.items.map((it) => ({
          key: it.key,
          icon: it.icon,
          label: <span data-testid={`tab-${it.key}`}>{t(it.label)}</span>,
        })),
      })),
    [t],
  );

  return (
    <Layout style={{ minHeight: '100dvh' }}>
      <Layout.Sider
        width={236}
        theme="dark"
        className="sidebar"
        collapsedWidth={0}
        collapsed={ciut}
        trigger={null}
      >
        <div className="sidebar-merek">
          <Avatar shape="square" size={30} style={{ background: '#14564d', fontSize: 11 }}>
            OSB
          </Avatar>
          <span className="sidebar-nama">{t('app.title')}</span>
        </div>
        {!ciut && (
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[layar]}
          items={items}
          onClick={(e) => {
            setLayar(e.key);
            /* ⭐ Di HP, memilih layar berarti selesai bernavigasi — menutupnya sendiri menghemat
               satu ketukan yang selalu dilakukan orang berikutnya. ⛔ Di desktop `ciut` tak pernah
               `true`, jadi baris ini tak berpengaruh sama sekali. */
            if (sempit) setMenuBuka(false);
          }}
        />
        )}
      </Layout.Sider>

      <Layout>
        <Layout.Header className="topbar">
          {/*
            ⭐ Tombol menu HANYA muncul saat sidebarnya memang tersembunyi. Tombol yang selalu ada
            di desktop cuma menambah benda yang tak pernah ditekan — dan yang tak pernah ditekan
            akan menutupi yang harus ditekan.
          */}
          {sempit && (
            <Button
              type="text"
              icon={<MenuOutlined />}
              data-testid="buka-menu"
              aria-label={t('nav.grpMaster')}
              onClick={() => { setMenuBuka((v) => !v); }}
            />
          )}
          <Tag data-testid="tenant-aktif">{s?.tenant ?? ''}</Tag>
          <Space size="middle" style={{ marginLeft: 'auto' }}>
            <span data-testid="siapa">
              <Typography.Text style={{ color: 'inherit' }}>{s?.displayName ?? ''}</Typography.Text>{' '}
              {/* ⭐ Peran ditampilkan: yang tak bisa menulis harus tahu SEBABNYA, bukan mengira rusak. */}
              <Tag data-testid="peran">{s?.role ?? ''}</Tag>
            </span>
            {/* ⭐ Melekat pada ORANGnya, bukan pada layar pemilik — semua peran butuh ini. */}
            <ChangePassword />
            <LanguageSwitcher />
            <Button data-testid="logout" onClick={() => { logout(); onLogout(); }}>
              {t('common.logout')}
            </Button>
          </Space>
        </Layout.Header>

        <Layout.Content className="isi-konsol">
          {layar === 'brands' && <Brands />}
          {layar === 'structure' && <Structure />}
          {layar === 'products' && <Products />}
          {layar === 'price-groups' && <PriceGroups />}
          {layar === 'taxes' && <Taxes />}
          {layar === 'availability' && <Availability />}
          {layar === 'promo' && <Promos />}
          {layar === 'integrations' && <Integrations />}
          {layar === 'routing' && <Routing />}
          {layar === 'payments' && <Payments />}
          {layar === 'receipt' && <ReceiptTemplate />}
          {layar === 'reports' && <Reports />}
          {layar === 'notifications' && <Notifications />}
          {layar === 'stock' && <Stock />}
          {layar === 'subscription' && <Subscription />}
          {layar === 'staff' && <Staff />}
          {layar === 'roles' && <Roles />}
          {layar === 'console-users' && <ConsoleUsers />}
          {layar === 'floor' && <Floor />}
          {layar === 'modifiers' && <Modifiers />}
          {layar === 'outlets' && <Outlets />}
        </Layout.Content>
      </Layout>
    </Layout>
  );
}
